// TOEFL iBT Mock 6 — Listening (2026 format, multistage adaptive, advanced)
//
// Task types:
//   response     – Listen and Choose a Response: hear one line, pick the best reply.
//   conversation – Listen to a Conversation (campus / everyday life).
//   announcement – Listen to an Announcement (campus or public notice).
//   talk         – Listen to an Academic Talk (short lecture).
//
// Like Mock 7, this set leans on indirect replies, implied meaning, speaker
// attitude and "why does the speaker mention…" questions, and wrong options
// often reuse words from the audio to punish word-matching.
//
// Each recording plays once. Questions appear after it finishes.
// gender ("m" / "f") picks the synthesized voice for each line.

export const MODULE_SECONDS = { module1: 15 * 60, module2: 12 * 60 };

export const TASK_LABELS = {
  response: "Choose a Response",
  conversation: "Conversation",
  announcement: "Announcement",
  talk: "Academic Talk",
};

const W = (text) => ({ speaker: "Woman", gender: "f", text });
const M = (text) => ({ speaker: "Man", gender: "m", text });

const response = (id, line, options, correct) => ({
  id,
  type: "response",
  lines: [line],
  questions: [{ question: "Choose the best response.", options, correct }],
});

export const listeningModules = {
  module1: [
    response("m1-r1", M("You didn't by any chance keep the receipt for the lab supplies?"), [
      "I kept the lab clean all week.",
      "It's in the folder on my desk.",
      "No, I didn't receive any supplies.",
      "By chance, I was in the lab.",
    ], 1),
    response("m1-r2", W("I'd go to the career fair if it weren't the same day as my exam."), [
      "The career fair was great last year.",
      "I didn't know you were taking a career exam.",
      "Couldn't you stop by for an hour after the exam?",
      "Yes, it's the same exam as mine.",
    ], 2),
    response("m1-r3", M("The professor said the final project could be done in pairs, didn't she?"), [
      "Only with her permission, I think.",
      "I've done a pair of projects already.",
      "She said the final was difficult.",
      "Yes, I paired up my socks.",
    ], 0),
    response("m1-r4", W("Don't tell me the printer's jammed again."), [
      "I told you about the printer yesterday.",
      "The jam is in the cafeteria.",
      "Again? I just printed my essay.",
      "Afraid so. Let's try the one in the lobby.",
    ], 3),
    response("m1-r5", M("I'm thinking of dropping statistics. It's way over my head."), [
      "The statistics are on page twelve.",
      "Have you tried the free tutoring center first?",
      "I dropped my phone over there.",
      "My head hurts too.",
    ], 1),
    response("m1-r6", W("Weren't you supposed to hear back about the internship by now?"), [
      "They pushed the decision back to next Friday.",
      "I heard the internship is in the back office.",
      "Yes, I was supposed to apply.",
      "Now is a good time for an internship.",
    ], 0),
    response("m1-r7", M("That's the third time this week the elevator's been out of order."), [
      "I ordered three of them this week.",
      "The elevator is on the third floor.",
      "Good thing we're only on the second floor.",
      "I've been out three times too.",
    ], 2),
    response("m1-r8", W("You might want to double-check those figures before you submit the report."), [
      "I submitted two reports already.",
      "The figures are in the appendix.",
      "I'll double my effort next time.",
      "Why? Did something look off to you?",
    ], 3),
    {
      id: "m1-conv",
      type: "conversation",
      title: "Registrar's Office",
      lines: [
        W("Hi, I need to withdraw from my organic chemistry course. I've fallen too far behind after being sick for three weeks."),
        M("I'm sorry to hear that. Just so you know, today is past the deadline for a full tuition refund. You'd get back fifty percent, and the course would show a W on your transcript."),
        W("A W? Would that hurt my application to the nursing program?"),
        M("A single W usually isn't a problem, but there's another option you might want to consider. Since you were ill, you could apply for a medical withdrawal instead. You'd need a letter from your doctor, but if it's approved, the course disappears from your transcript completely, and you'd get a full refund."),
        W("Oh, I didn't know that existed. How long does it take?"),
        M("The committee meets every two weeks, so it could take up to a month. The important thing is that you submit the request before the last day of classes. After that, only the regular withdrawal is possible."),
        W("Then I'll call my doctor's office this afternoon."),
      ],
      questions: [
        {
          question: "Why does the woman want to withdraw from the course?",
          options: [
            "She wants to transfer to the nursing program.",
            "Her illness caused her to fall behind.",
            "She cannot afford the tuition.",
            "She is unhappy with the professor.",
          ],
          correct: 1,
        },
        {
          question: "What is an advantage of a medical withdrawal over a regular withdrawal?",
          options: [
            "It is processed on the same day.",
            "It does not require any documents.",
            "It removes the course from the transcript.",
            "It allows her to retake the course for free.",
          ],
          correct: 2,
        },
        {
          question: "What does the man imply about the timing of her request?",
          options: [
            "She must apply before classes end to keep the medical option.",
            "She has already missed the deadline for a medical withdrawal.",
            "The committee will meet only once more this semester.",
            "She should wait until she has recovered completely.",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "m1-ann",
      type: "announcement",
      title: "Campus Water Notice",
      lines: [
        M("This is a notice from Facilities Management. Routine testing has found elevated levels of lead in the drinking fountains of two older buildings: Harmon Hall and the east wing of the Fine Arts Center. As a precaution, all fountains in those buildings have been shut off, and bottled water is available at each building's main entrance. Please note that tap water in the residence halls is supplied by a separate system and has tested well within safe limits, so no action is needed there. Replacement of the affected pipes will begin during the winter break. Until then, students and staff in the two buildings should not use bathroom taps for drinking, even though handwashing remains safe."),
      ],
      questions: [
        {
          question: "What is the main purpose of the announcement?",
          options: [
            "To announce the closure of two campus buildings",
            "To explain a water safety problem and the steps being taken",
            "To ask students to stop using residence hall taps",
            "To describe the results of a winter construction project",
          ],
          correct: 1,
        },
        {
          question: "What can be inferred about the water in the residence halls?",
          options: [
            "It comes from a different source than the affected buildings.",
            "It will be tested again during the winter break.",
            "It is safe for handwashing but not for drinking.",
            "It is being replaced with bottled water.",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "m1-talk",
      type: "talk",
      title: "Astronomy: Finding Planets by Their Shadows",
      lines: [
        W("Planets around other stars are almost impossible to see directly. A star is billions of times brighter than the planets orbiting it, so any reflected light from a planet gets lost in the glare. Astronomers have therefore had to find indirect methods, and the most productive one by far is the transit method. If a planet's orbit happens to be lined up with our view, then once per orbit the planet passes in front of its star and blocks a tiny fraction of the starlight. We can't see the planet, but we can measure the star dimming, sometimes by less than one hundredth of one percent. The amount of dimming tells us the planet's size relative to the star, and the time between transits tells us how long its year is. Now, there's an obvious limitation. Only planets whose orbits are aligned almost exactly edge-on to us will ever transit. For an Earth-like planet around a Sun-like star, the chance of that alignment is about half a percent. That's why missions like Kepler watched more than one hundred thousand stars at once: if you're relying on lucky alignments, you need an enormous sample. And to confirm that a dip in brightness really is a planet and not, say, a spot on the star, astronomers require at least three transits at perfectly regular intervals."),
      ],
      questions: [
        {
          question: "Why is it difficult to observe planets around other stars directly?",
          options: [
            "Planets move too quickly across the sky.",
            "The light from the star overwhelms the light from the planet.",
            "Most planets are hidden behind their stars.",
            "Telescopes cannot measure reflected light.",
          ],
          correct: 1,
        },
        {
          question: "What information does the time between transits provide?",
          options: [
            "The size of the planet",
            "The temperature of the star",
            "The length of the planet's orbit",
            "The distance between the planet and Earth",
          ],
          correct: 2,
        },
        {
          question: "Why does the professor mention that Kepler watched over one hundred thousand stars?",
          options: [
            "To show that most stars have Earth-like planets",
            "To compare Kepler with telescopes on the ground",
            "To argue that the transit method is no longer useful",
            "To explain how a rare alignment can still produce many discoveries",
          ],
          correct: 3,
        },
        {
          question: "Why do astronomers require at least three regular transits?",
          options: [
            "To measure how bright the planet is",
            "To calculate the age of the star",
            "To rule out other possible causes of the dimming",
            "To prove that the planet has an atmosphere",
          ],
          correct: 2,
        },
      ],
    },
  ],

  // ── Harder Module 2 ──────────────────────────────────────────
  upper: [
    response("up-r1", W("I wouldn't count on the library being quiet this close to finals."), [
      "I counted twelve people in the library.",
      "Then maybe I'll try the study rooms in the student center.",
      "Finals are quieter than midterms.",
      "I'd rather not count on you either.",
    ], 1),
    response("up-r2", M("You could've told me the meeting had been cancelled."), [
      "I tried calling, but your phone was off.",
      "The meeting was told to cancel.",
      "Yes, I could tell it was cancelled.",
      "I've never been to that meeting room.",
    ], 0),
    response("up-r3", W("It's not that I don't like the topic; I just doubt there's enough research on it."), [
      "I like the topic too.",
      "Research is done in the lab upstairs.",
      "Why don't you check the databases before you decide?",
      "I don't doubt that you like it.",
    ], 2),
    response("up-r4", M("So much for finishing the experiment before the weekend."), [
      "The weekend was very relaxing.",
      "I finished so much last weekend.",
      "Thanks so much for the experiment.",
      "What went wrong? Was it the equipment again?",
    ], 3),
    response("up-r5", W("Only after reading the reviewers' comments did I realize how unclear my argument was."), [
      "Reviewers are usually right about argument.",
      "At least now you know exactly what to fix.",
      "I read the comments only after the argument.",
      "It's clear that you read a lot.",
    ], 1),
    {
      id: "up-conv",
      type: "conversation",
      title: "Career Counselor",
      lines: [
        M("Thanks for seeing me. I've been offered a job at an engineering firm, but I've also been accepted into a master's program, and both start in September."),
        W("Congratulations, that's a good problem to have. Have you asked either of them about a deferral?"),
        M("The university said I could defer for one year. I haven't asked the firm, because I assumed they'd just withdraw the offer."),
        W("That's not necessarily true. Firms in your field often value employees with graduate degrees, and some even pay part of the tuition if you study part-time while working. It's worth asking, as long as you frame it carefully."),
        M("Carefully how?"),
        W("Don't present it as if you're hesitating about the job. Say you're committed to working for them, and ask whether they support further study. Their answer will tell you a lot about how they treat their employees."),
        M("That's a good point. I hadn't thought of it as a way to learn about the company."),
      ],
      questions: [
        {
          question: "What is the man's main problem?",
          options: [
            "He was rejected by a master's program.",
            "He has two opportunities that begin at the same time.",
            "He cannot afford graduate school tuition.",
            "His job offer was withdrawn.",
          ],
          correct: 1,
        },
        {
          question: "What does the counselor imply about the man's assumption?",
          options: [
            "It may be wrong, so he should talk to the firm.",
            "It is correct for most engineering firms.",
            "It shows he does not really want the job.",
            "It is the reason the university allowed a deferral.",
          ],
          correct: 0,
        },
        {
          question: "Why does the counselor advise the man to say he is committed to the job?",
          options: [
            "So that the firm will offer him a higher salary",
            "So that the university will extend his deferral",
            "So that his question does not seem like doubt about the offer",
            "So that he can start the master's program sooner",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "up-talk",
      type: "talk",
      title: "Linguistics: Reviving Endangered Languages",
      lines: [
        M("Linguists estimate that around half of the world's roughly seven thousand languages may stop being spoken by the end of this century. A language rarely disappears because people are forbidden to speak it, though that has certainly happened. More often, parents decide that their children will have better opportunities in a dominant language, and the chain of transmission from one generation to the next quietly breaks. Once that happens, the remaining speakers are mostly elderly, and the language has very little time left. Reversing this is extremely difficult, but not impossible. In New Zealand, for example, the number of children speaking Māori had fallen dramatically by the nineteen seventies. Community leaders responded by creating what they called language nests: preschools where elders spoke only Māori with very young children. The idea was that children acquire a language most easily before the age of five, and that the elders, who were the last fluent speakers, could pass it on directly. Those children later demanded schools that continued teaching in Māori, and some of them are now raising their own children in the language. Notice that the program succeeded not because it taught the language as a school subject, but because it rebuilt the link between generations."),
      ],
      questions: [
        {
          question: "According to the professor, what is the most common reason languages disappear?",
          options: [
            "Governments ban them from being spoken.",
            "Parents choose not to pass them on to their children.",
            "They are not taught as school subjects.",
            "Linguists fail to record them in time.",
          ],
          correct: 1,
        },
        {
          question: "Why were elders important to the language nests?",
          options: [
            "They had the most experience as teachers.",
            "They could raise money for the preschools.",
            "They were among the last fluent speakers.",
            "They wanted children to learn the dominant language.",
          ],
          correct: 2,
        },
        {
          question: "What does the professor imply about teaching a language as a school subject?",
          options: [
            "It is the most effective way to save a language.",
            "It should begin before the age of five.",
            "It was the main reason the Māori program succeeded.",
            "On its own, it may not be enough to revive a language.",
          ],
          correct: 3,
        },
        {
          question: "Why does the professor mention that some former students are raising their children in Māori?",
          options: [
            "To show that transmission between generations was restored",
            "To criticize parents who did not speak Māori",
            "To explain why the schools were later closed",
            "To give an example of a language that disappeared",
          ],
          correct: 0,
        },
      ],
    },
  ],

  // ── Easier Module 2 ──────────────────────────────────────────
  lower: [
    response("low-r1", M("Where can I buy a parking permit?"), [
      "I parked near the gym.",
      "At the campus security office.",
      "It costs a lot of money.",
      "Yes, I have a permit.",
    ], 1),
    response("low-r2", W("Are you ready for the history quiz tomorrow?"), [
      "History is my favorite subject.",
      "The quiz was yesterday.",
      "Almost. I just need to review chapter four.",
      "I'm ready to go home.",
    ], 2),
    response("low-r3", M("Can I borrow your pen for a second?"), [
      "Sure, here you go.",
      "I bought it for a second time.",
      "The pen is blue.",
      "No, it's my second class.",
    ], 0),
    response("low-r4", W("What did you think of the movie?"), [
      "It starts at eight.",
      "I watched it with my sister.",
      "The tickets were cheap.",
      "It was much better than I expected.",
    ], 3),
    response("low-r5", M("Is the computer lab open on Sundays?"), [
      "I like the new computers.",
      "Only in the afternoon, I think.",
      "The lab is on Main Street.",
      "Yes, I opened it.",
    ], 1),
    {
      id: "low-ann",
      type: "announcement",
      title: "Bookstore Announcement",
      lines: [
        W("Attention, students. If you rented textbooks from the campus bookstore this semester, please return them by Friday, December twelfth. Books can be returned at the bookstore counter or placed in the drop box outside the main entrance. Please remove any loose papers from the books before you return them. If a book is returned late, you will be charged a fee of ten dollars per day. Thank you, and good luck on your final exams."),
      ],
      questions: [
        {
          question: "What is the announcement mainly about?",
          options: [
            "A sale on new textbooks",
            "Returning rented textbooks",
            "The bookstore's new opening hours",
            "Final exam schedules",
          ],
          correct: 1,
        },
        {
          question: "What will happen if a student returns a book late?",
          options: [
            "The student will pay a daily fee.",
            "The student cannot take final exams.",
            "The student must buy the book.",
            "The student will lose library access.",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "low-talk",
      type: "talk",
      title: "Biology: The Honeybee Dance",
      lines: [
        M("Honeybees have a surprising way of sharing information: they dance. When a worker bee finds a good source of flowers, it returns to the hive and performs what scientists call the waggle dance. The bee walks in a straight line while shaking its body from side to side, then circles back and repeats the movement. The direction of the straight line shows the other bees which way to fly, compared with the position of the sun. The length of time the bee waggles tells them how far away the flowers are. A longer waggle means the flowers are farther away. Other bees follow the dancer closely, and then they fly out to find the flowers themselves. This dance was first explained by an Austrian scientist named Karl von Frisch, who later won a Nobel Prize for his work."),
      ],
      questions: [
        {
          question: "What is the talk mainly about?",
          options: [
            "How bees make honey",
            "Why bees live in large groups",
            "How bees share the location of food",
            "How flowers attract bees",
          ],
          correct: 2,
        },
        {
          question: "What does the direction of the straight line in the dance show?",
          options: [
            "Which way to fly to the flowers",
            "How many flowers there are",
            "What color the flowers are",
            "How old the dancing bee is",
          ],
          correct: 0,
        },
        {
          question: "What does a longer waggle mean?",
          options: [
            "The flowers are very sweet.",
            "The bee is tired.",
            "There is danger near the hive.",
            "The flowers are farther away.",
          ],
          correct: 3,
        },
        {
          question: "Who was Karl von Frisch?",
          options: [
            "A beekeeper who sold honey",
            "A doctor who treated bee stings",
            "A dancer who studied insects",
            "A scientist who explained the bee dance",
          ],
          correct: 3,
        },
      ],
    },
  ],
};
